import { SharePointApiError } from '@/lib/api/sharepoint.js';

/**
 * The words a 403 is said in when the service sent none of its own. It repeats the service's sentence
 * deliberately: `cti-sync-service` answers 403 for exactly one thing — a `connection_id` that is not one
 * of the caller's SharePoint connections — and the way out is to pick one that is.
 */
const CONNECTION_NOT_AVAILABLE =
  'That SharePoint connection is not available to you. Pick one of the SharePoint connections this workspace offers you and try again.';

/**
 * What a browsing surface says when the caller's SharePoint connections could not be read at all. It
 * browses nothing then: a listing that names no connection is not an answer about this caller.
 */
export const SHAREPOINT_PRESENCE_UNKNOWN =
  "We couldn't check which SharePoints you can reach. Reload the page and try again.";

/**
 * The sentence the service sent with the failure, or `null` when it sent none. An empty error body is
 * filled on this side with a `… (<status>)` label, which says what failed and nothing about what to do,
 * so it counts as no sentence at all.
 */
function serverSentence(error: unknown, status: number): string | null {
  if (!(error instanceof SharePointApiError)) return null;
  const message = error.message.trim();
  if (message === '' || message.endsWith(`(${status})`)) return null;
  return message;
}

/**
 * What to tell the user about a failed SharePoint browse. The status decides, because the interesting
 * ones mean completely different things and the wrong explanation costs real time — every failure once
 * claimed a missing permission, which sent a live investigation looking for an access problem that did
 * not exist, and 403 then claimed the session had expired, which it never means here.
 *
 * Browsing is gated on workspace MEMBERSHIP plus the connection the caller named, so:
 * **401** is a caller with no usable token (`JwtAuthGuard` answers that, not 403), **403** is a
 * SharePoint connection that is not one of this caller's — the service's own sentence is preferred,
 * because it is the one that names the way out — **404** is a workspace the caller is not a member of
 * (it is invisible to them rather than forbidden), and **412** is THIS CALLER holding no SharePoint
 * connection, the same status the knowledge picker's connect gate uses. Anything else is a genuine Graph
 * or transport failure and says so, with the server's own message.
 *
 * The 412 sentence used to blame the workspace. Everybody connects under their own account now, so the
 * workspace may well reach several SharePoints while this reader reaches none — and the sentence says
 * that, plus the part people do not expect: the knowledge base a colleague filled stays searchable
 * either way, within what this reader may open in SharePoint.
 */
export function browseErrorText(error: unknown): string {
  const status = error instanceof SharePointApiError ? error.status : undefined;
  if (status === 412) {
    return "You haven't connected SharePoint yet. Everybody connects under their own account — connect yours in the workspace's Connectors settings, then try again. Your colleagues' workspace knowledge stays searchable, within your own SharePoint access.";
  }
  if (status === 403) {
    return serverSentence(error, 403) ?? CONNECTION_NOT_AVAILABLE;
  }
  if (status === 401) {
    return "You're not signed in any more. Sign in again to browse SharePoint.";
  }
  if (status === 404) {
    return "Couldn't find this workspace. Only its members can browse the SharePoint connected to it.";
  }
  const detail =
    error instanceof Error && error.message.trim() ? ` ${error.message}` : '';
  return `Couldn't load from SharePoint.${detail}`;
}

/**
 * Whether a browse failed because the CALLER has no SharePoint connection. That is not an error to
 * report — it is an invitation to connect one, so the surfaces render it as such.
 *
 * It is 412 and nothing else. A 403 is a connection the caller may not use, which is a refusal and not a
 * missing step: rendering it as the invitation would send someone to connect a SharePoint the workspace
 * already has.
 */
export function isSharePointNotConnected(error: unknown): boolean {
  return error instanceof SharePointApiError && error.status === 412;
}

/**
 * Whether a place read refused the connection as no longer in the caller's lane
 * (`SHAREPOINT_PLACE_ACCESS_REVOKED`). Coverage and the places strip then know nothing about it: the
 * surfaces read that as "unknown" and stay quiet, because it is a fact about whose connection this is,
 * not a failure the reader can fix by retrying.
 */
export function isSharePointPlaceAccessRevoked(error: unknown): boolean {
  return (
    error instanceof SharePointApiError &&
    error.code === 'SHAREPOINT_PLACE_ACCESS_REVOKED'
  );
}
